import React, { useEffect, useMemo, useState } from "react";
import CoworkerSelectionField from 'scenes/home/coworker/filter/SelectionField';
import Text from 'theme/Text';
import CheckBox from 'theme/CheckBox'
import { useIsFocused, useNavigation } from "@react-navigation/native";
import { StyleSheet, View } from "react-native";
import i18n from "utils/i18n";
import { useDispatch, useSelector } from "react-redux";
import { dataConstants } from "services/redux/data/constants";
import { setData } from "services/redux/data/actions";
import ThemedBlock from "components/atoms/ThemedBlock";
import { AppRoutes } from "navigations/app-routes";
import { styles } from "../../filter/_styles"
import Button from "theme/Button";
import _ from "lodash";
import FetchEmployee from "utils/fetch/FetchEmployee";
import HelperSetting from "utils/helper-setting";
import { settingsConstants } from "utils/constants/settings-constants";
import HelperString from 'utils/helper-string'
import ThemedList from 'components/atoms/ThemedList'
import ThemedPressable from 'components/atoms/ThemedPressable'
import ThemedButtonContainer from 'components/atoms/ThemedButtonContainer'
import ThemedInputSearch from 'components/atoms/ThemedInputSearch'
import { TESTID } from '_shared/config/testID'
import Icon from 'components/atoms/Icon'
import { IconSystemConstants } from 'utils/constants/IconNameConstants'
import { layoutStyles } from 'styles/layout'
import HelperArray from 'utils/helper-array'
import VStack from 'components/atoms/stack/VStack'
import { ThemedValues } from 'styles/themed'

const ResetInputIcon = ({ onPress, props }) => {
	return (
		<ThemedPressable
			onPress={ onPress }>

			<Icon icon={ IconSystemConstants.INPUT_RESET } { ...props } />
		</ThemedPressable>
	);
};

const FilterTab = () => {
	
	const navigation = useNavigation();
	const isFocused = useIsFocused();
	const dispatch = useDispatch()
	
	const maxFilterResults = 200;
	
	const [ filters, setFilters ] = useState( [] )
	const [ filteredEmployees, setFilteredEmployees ] = useState( [] )
	const [ filteredEmployeesFetching, setFilteredEmployeesFetching ] = useState( false )
	
	const _selectedFilterOptions = useSelector( state => {
		return state.data[dataConstants.SELECTED_FILTER_OPTIONS];
	} )
	const _filters = useSelector( state => {
		return state.data[dataConstants.FILTERS];
	} )
	
	const isFiltering = useMemo( () => {
		if( typeof _selectedFilterOptions === 'undefined' ) {
			return false;
		}
		
		if( _.size( _selectedFilterOptions ) === 0 ) {
			return false;
		}
		
		let isFiltering = false;
		_.forEach( _selectedFilterOptions, ( selectedFilterOption ) => {
			if( _.size( selectedFilterOption ) > 0 ) {
				isFiltering = true;
				return false;
			}
		})
		
		return isFiltering;
	}, [ _selectedFilterOptions ] )
	
	const fetchEmployeesFiltered = () => {
		if( isFocused && isFiltering ) {
			FetchEmployee.fetchFiltered( {
				name: _.get( _selectedFilterOptions, 'name', null ),
				company_guid: _.get( _selectedFilterOptions, 'company_guid', null ),
				department_guid: _.get( _selectedFilterOptions, 'department_guid', null ),
				team_guid: _.get( _selectedFilterOptions, 'team_guid', null ),
				jobtitle_guid: _.get( _selectedFilterOptions, 'jobtitle_guid', null ),
				callbackStart: setFilteredEmployeesFetching.bind( this, true ),
				callbackError: setFilteredEmployeesFetching.bind( this, false ),
				callbackSuccess: fetchedEmployeesFiltered
			} );
		}
	}
	
	const renderItem = ({ item, index }) => {
		let component = null;
		let componentProps = {
			shadow: true
		}
		
		switch( item.type ) {
			case 'component':
				component = item.component;
				break;
			
			case 'input':
				const value = _.get( _selectedFilterOptions, item.identifier, null );
				
				let icon = null;
				if( value !== null && value.length > 0 ) {
					icon = ( props ) => {
						return (
							<ResetInputIcon
								{ ...props }
								onPress={ inputReset.bind( this, item.identifier ) }
								props={ props }
							/>
						);
					}
				}
				
				componentProps.transparent = true
				componentProps.shadow = false
				
				component = (
					<ThemedInputSearch
						initialValue={ value }
						placeholder={ _.get( item, 'placeholder', '' ) }
						onChangeText={ _.debounce( inputChanged.bind( this, item.identifier ), 500 ) }
						accessoryRight={ icon }
						style={ styles.input }
						returnKeyType={ "search" }
						onSubmitEditing={ fetchEmployeesFiltered.bind( this ) }
						testID={ `${ TESTID.HOME.COWORKER.SEARCH.SEARCH_INPUT}_${ item.identifier }` }
					/>
				)
				break;
			
			case 'checkboxes':
				const checkboxes = _.map( item.options, ( option, key ) => {
					return (
						<CheckBox
							checked={ true }
							style={ [ styles.checkBox, key == item.options.length - 1 ? styles.checkBoxLast : [] ] }
							testID={ `${ TESTID.HOME.COWORKER.SEARCH.SEARCH_CHECKBOX}_${ item.indentifier }` }>
							
							{ option.title}
						</CheckBox>
					)
				});
				
				component = (
					<View>{ checkboxes }</View>
				);
				break;
			
			case 'selection':
				componentProps.transparent = true;
				componentProps.shadow = false;
				const onPress = () => {
					openFilterSelection( item.identifier, item.title, item.options );
				};
				
				const selected_items = [];
				_.forEach( item.options, ( option, key ) => {
					let is_checked = false;
					const selectedOptions = _.get( _selectedFilterOptions, item.identifier, []);
					
					for( const guid_key in option.guids ) {
						if( selectedOptions.indexOf( option.guids[ guid_key ] ) !== -1 ) {
							is_checked = true;
						}
					}
					
					if( is_checked ) {
						selected_items.push( option.title );
					}
				});
				
				component = (
					<CoworkerSelectionField
						title={ item.title }
						onPress={ onPress }
						selection={ selected_items.length > 0 ? HelperArray.naturalJoin( selected_items ) : null }
					/>
				);
				break;
		}
		
		return (
			<View
				style={staticStyles.viewMarginBottomStackSpace}>
				
				<ThemedBlock
					key={ HelperString.hash( 'filter', item.title ) }
					style={ styles.filterContainer }
					{ ...componentProps }>
					
					{ component }
				</ThemedBlock>
			</View>
		);
	}
	
	const createFilters = () => {
		const availableFilters = [ {
			identifier: 'name',
			title: i18n.t( 'page.coworker.filters.filter_names.name' ),
			type: 'input',
			placeholder: i18n.t( 'page.coworker.filters.filter_names.name')
		}];
		
		_.forEach( _filters, ( options, filter_key ) => {
			if( filter_key !== 'none' ) {
				
				const availableOptions = [];
				
				switch ( true ) {
					case filter_key === 'company_guid' && HelperSetting.isNo( settingsConstants.COWORKER_SEARCH_COMPANY ):
					case filter_key === 'team_guid' && HelperSetting.isNo( settingsConstants.COWORKER_SEARCH_TEAM ):
					case filter_key === 'department_guid' && HelperSetting.isNo( settingsConstants.COWORKER_SEARCH_DEPARTMENT ):
					case filter_key === 'jobtitle_guid' && HelperSetting.isNo( settingsConstants.COWORKER_SEARCH_JOBTITLE ):
						return true;
				}
				
				_.forEach( options, ( option ) => {
					availableOptions.push( {
						guids: option.guids,
						title: option.name === null ? i18n.t( 'ui.unknown' ) : option.name
					} )
				} );
				
				availableFilters.push( {
					identifier: filter_key,
					title: i18n.t( `page.coworker.filters.filter_names.${ filter_key }` ),
					type: 'selection',
					options: availableOptions
				} );
			}
		});
		
		setFilters( availableFilters )
	}
	
	const inputReset = ( identifier ) => {
		const options = _.clone( _selectedFilterOptions )
		delete options[ identifier ];
		
		dispatch( setData( dataConstants.SELECTED_FILTER_OPTIONS, options ) )
	}
	
	const inputChanged = ( identifier, value ) => {
		let options = _.clone( _selectedFilterOptions )
		if( typeof options !== 'object' ) {
			options = {};
		}
		
		options[ identifier ] = value;
		
		dispatch( setData( dataConstants.SELECTED_FILTER_OPTIONS, options ) )
	}
	
	const openFilterSelection = ( identifier, title, options ) => {
		navigation.navigate( AppRoutes.COWORKERS_FILTER_SELECTION, {
			identifier: identifier,
			title: title,
			options: options
		} );
	}
	
	const applyFilterOptions = ( payload ) => {
		console.log('testy5', 'payload', payload );
		dispatch( setData( dataConstants.APPLIED_FILTERS, payload ) );
		dispatch( setData( dataConstants.APPLIED_FILTERS_CHANGED, true ) )
	}
	
	const viewResults = () => {
		applyFilterOptions( _selectedFilterOptions );
		dispatch( setData( dataConstants.FILTER_RESULTS, filteredEmployees ) )
		
		navigation.navigate( AppRoutes.COWORKERS_FILTER_RESULTS );
	}
	
	const removeFilter = () => {
		applyFilterOptions( {} );
		dispatch( setData( dataConstants.SELECTED_FILTER_OPTIONS, {} ) )
		dispatch( setData( dataConstants.FILTER_RESULTS, filteredEmployees ) )
	}
	
	const fetchedEmployeesFiltered = ( result ) => {
		setFilteredEmployees( _.get( result, [ 'employees' ], [] ) )
		setFilteredEmployeesFetching( false )
		
		dispatch( setData( dataConstants.APPLIED_FILTERS_CHANGED, false ) )
	}
	
	useEffect( () => {
		return fetchEmployeesFiltered();
	}, [ isFocused, _selectedFilterOptions ] )
	
	useEffect( () => {
		return createFilters();
	}, [ _filters ] )
	
	const buttons = useMemo( () => {
		if( filteredEmployeesFetching ) {
			return (
				<Button
					style={ styles.okButton }
					disabled
					isLoading={ true }>
					
					{ i18n.t( 'page.coworker.filters.searching' ) }
				</Button>
			)
		}
		
		if( isFiltering ) {
			const content = [];
			if( filteredEmployees.length < maxFilterResults ) {
				content.push(
					<Button
						style={ styles.okButton }
						onPress={ viewResults }
						disabled={ filteredEmployees.length === 0 }
						isLoading={ filteredEmployeesFetching }
						testID={ TESTID.HOME.COWORKER.SEARCH.SHOW_RESULTS_BUTTON }>
						
						{ i18n.t( 'page.coworker.filters.view_results', { amount: filteredEmployees.length } ) }
					</Button>
				)
			} else {
				content.push(
					<Button
						style={ styles.okButton }
						disabled={ true }
						testID={ TESTID.HOME.COWORKER.SEARCH.TOO_MANY_RESULTS_BUTTON }>
						
						{ i18n.t( 'page.coworker.filters.too_many_results', { amount: maxFilterResults } ) }
					</Button>
				)
			}
			
			content.push(
				<Button
					appearance={ "ghost" }
					status={ "danger" }
					onPress={ removeFilter }
					disabled={ !isFiltering }
					style={ styles.okButton }
					testID={ TESTID.HOME.COWORKER.SEARCH.RESET_BUTTON }>
					
					{ i18n.t( 'page.coworker.filters.reset' ) }
				</Button>
			)
			
			return content;
		}
		
		return (
			<Text
				style={ styles.emptyHint }
				testID={ TESTID.HOME.COWORKER.SEARCH.NO_FILTERS_BUTTON }>
				
				{ i18n.t( 'page.coworker.filters.no_filters' ) }
			</Text>
		)
	}, [ filteredEmployeesFetching, isFiltering, filteredEmployees, maxFilterResults ] )

	return (
		<View
			style={ layoutStyles.flexOne }>
			
			<VStack
				space={ ThemedValues.stackSpace }>
				
				<ThemedList
					style={ styles.listContainer }
					data={ filters }
					renderItem={ renderItem.bind( this ) }
					dividerEnabled={ false }
					scrollEnabled={ false }
				/>
	
				<ThemedButtonContainer
					padding={ false }>
	
					{ buttons }
				</ThemedButtonContainer>
			</VStack>
		</View>
	);
}

const staticStyles = StyleSheet.create({
	viewMarginBottomStackSpace: {
		marginBottom: ThemedValues.stackSpace,
	},
});

export default FilterTab
